/**
 * Component → render proxy translators (architecture §6.1, review F-5).
 *
 * Each translator is a pure function of an entity's component data (and, for
 * a Canvas, its UI element children and its ancestors' content), so it's
 * testable without a GPU.
 * These are the only code that knows how a Placeholder becomes a capsule or a
 * Role becomes panel text. The render backend only ever sees ProxyDesc.
 *
 * Components with no translator (Transform, Zone, Role, …) don't draw. A
 * Camera draws only its editor gizmo, which Game View and previews leave out.
 */
import { engineShapeOf } from "../../assets/engineAssets";
import type { ProxyDesc, WorldUiItem, WorldUiLine } from "../../render/proxies";
import type {
  Animator,
  Camera,
  Canvas,
  Image,
  Light,
  MeshRenderer,
  Placeholder,
  Text,
  VFX,
} from "../../world/components";
import { enabledComponent } from "../../world/entity";
import type { EntityRecord } from "../../world/entity";
import type {
  ComponentData,
  ComponentSchema,
  InferComponentData,
} from "../../world/schema/defineComponent";
import type { WorldReader } from "../../world/World";
import { V1_COMPONENTS } from "../../world/components";
import { bindsList, boundParagraphs } from "./bindings";
import type { BindingSource } from "./bindings";
import { isPlaceholder } from "../format/placeholder";

export interface ComponentTranslator {
  readonly component: string;
  /**
   * Components on child entities this translator reads (a Canvas draws its UI
   * elements): changes to them, and to the children themselves, re-translate it.
   */
  readonly readsChildren?: readonly string[];
  /** Reads content anywhere up its ancestors (Text bindings): any change to them re-translates it. */
  readonly readsAncestors?: boolean;
  /** Other components on the same entity this translator reads; changes to them re-translate it. */
  readonly readsSelf?: readonly string[];
  translate(entity: EntityRecord, data: ComponentData, world: WorldReader): ProxyDesc;
}

/** A component's stored data, typed from its schema. */
type Data<S extends ComponentSchema> = InferComponentData<S>;

// ─── Geometry ────────────────────────────────────────────────────────────

const placeholder: ComponentTranslator = {
  component: "Placeholder",
  translate: (_entity, raw) => {
    const data = raw as Data<typeof Placeholder>;
    const diameter = data.radius * 2;
    const size = {
      capsule: [diameter, data.height, diameter],
      box: [diameter, data.height, diameter],
      plane: [data.height, 0.01, data.height],
    }[data.shape] as [number, number, number];

    return {
      kind: "mesh",
      primitive: data.shape,
      asset: null,
      size,
      material: "placeholder",
      color: { theme: "wireframe" },
      castShadow: false,
      animation: null,
      background: false,
    };
  },
};

const meshRenderer: ComponentTranslator = {
  component: "MeshRenderer",
  // Like Unreal's skeletal mesh component, the mesh plays its own animation.
  readsSelf: ["Animator"],
  translate: (entity, raw) => {
    const data = raw as Data<typeof MeshRenderer>;
    const animator = entity.components.get("Animator") as Data<typeof Animator> | undefined;
    // An engine shape is drawn as a primitive; anything else is a model to stream.
    const shape = data.mesh === null ? null : engineShapeOf(data.mesh);
    const model = data.mesh !== null && shape === null ? data.mesh : null;
    return {
      kind: "mesh",
      primitive: shape,
      asset: model,
      size: data.size,
      material: data.material,
      color: data.color,
      castShadow: data.castShadow,
      animation: animator
        ? {
            clip: animator.clip,
            playing: animator.playing,
            loop: animator.loop,
            speed: animator.speed,
          }
        : null,
      background: data.background,
    };
  },
};

// ─── Cameras ─────────────────────────────────────────────────────────────

/** The camera gizmo: the backend draws the body and frustum from the field of view. */
const camera: ComponentTranslator = {
  component: "Camera",
  translate: (_entity, raw) => {
    const data = raw as Data<typeof Camera>;
    return { kind: "camera", fov: data.fov };
  },
};

// ─── Lights and effects ──────────────────────────────────────────────────

const light: ComponentTranslator = {
  component: "Light",
  translate: (_entity, raw) => {
    const data = raw as Data<typeof Light>;
    return {
      kind: "light",
      light: data.kind,
      color: data.color,
      intensity: data.intensity,
      range: data.range,
      angle: data.angle,
      castShadow: data.castShadow,
    };
  },
};

const vfx: ComponentTranslator = {
  component: "VFX",
  translate: (_entity, raw) => {
    const data = raw as Data<typeof VFX>;
    return {
      kind: "vfx",
      effect: data.effect,
      radius: data.radius,
      count: data.count,
      color: data.color,
    };
  },
};

// ─── In-world UI ─────────────────────────────────────────────────────────

/**
 * A Canvas draws its UI element children, top to bottom in Outliner order.
 * Hidden children and turned-off elements are left out, like anything hidden.
 */
const canvas: ComponentTranslator = {
  component: "Canvas",
  readsChildren: ["Text", "Image"],
  readsAncestors: true,
  translate: (entity, raw, world) => {
    const data = raw as Data<typeof Canvas>;
    const children = entity.children
      .map((id) => world.get(id))
      .filter((child): child is EntityRecord => child?.visible === true);
    const source = ancestorSource(entity, world);

    return {
      kind: "worldUI",
      width: data.width,
      height: data.height,
      background: data.background,
      items: children.flatMap((child) => uiItemsOf(child, source)),
    };
  },
};

/** What one UI element child draws: its Text's paragraphs (bindings filled), or its Image. */
function uiItemsOf(child: EntityRecord, source: BindingSource): WorldUiItem[] {
  const on = (type: string) => enabledComponent(child, type) !== undefined;

  if (on("Text")) {
    const text = child.components.get("Text") as Data<typeof Text>;
    const bullets = bindsList(text.text, source);
    return boundParagraphs(text.text, source).map((paragraph) => ({
      ...line(paragraph, text.style),
      ...(bullets && { bullet: true }),
    }));
  }
  if (on("Image")) {
    const image = child.components.get("Image") as Data<typeof Image>;
    return image.image === null ? [] : [{ image: image.image, height: image.height }];
  }
  return [];
}

/**
 * Bindings resolve up from the Canvas: the nearest entity with the component
 * (usually the zone holding the Role or Project), as a Text shows its zone.
 */
function ancestorSource(canvas: EntityRecord, world: WorldReader): BindingSource {
  return {
    find: (component) => {
      const schema = SCHEMAS.get(component);
      if (!schema) return null;
      for (
        let at: EntityRecord | undefined = canvas;
        at;
        at = at.parent ? world.get(at.parent) : undefined
      ) {
        const data = enabledComponent(at, component);
        if (data) return { data, schema };
      }
      return null;
    },
  };
}

/** Schemas by component type, for formatting bound values. */
const SCHEMAS = new Map(V1_COMPONENTS.map((schema) => [schema.type, schema]));

function line(text: string, style: WorldUiLine["style"]): WorldUiLine {
  return { text, style: isPlaceholder(text) ? "placeholder" : style };
}

// ─── Registry ────────────────────────────────────────────────────────────

export const V1_TRANSLATORS: readonly ComponentTranslator[] = [
  placeholder,
  meshRenderer,
  light,
  vfx,
  canvas,
  camera,
];

const BY_COMPONENT = new Map(
  V1_TRANSLATORS.map((translator) => [translator.component, translator]),
);

export function translatorFor(component: string): ComponentTranslator | undefined {
  return BY_COMPONENT.get(component);
}
